import React, { ReactNode } from "react";
import { Button, SxProps, Theme, ButtonProps } from "@mui/material";

interface ActionButtonProps {
  onClick?: () => void;
  children: ReactNode;
  disabled?: boolean;
  sticky?: boolean;
  variant?: "text" | "outlined" | "contained";
  color?: "primary" | "secondary" | "error" | "info" | "success" | "warning";
  customColor?: string;
  width?: string | { [key: string]: string };
  sx?: SxProps<Theme>;
  type?: "button" | "submit" | "reset";
  href?: string;
}

/**
 * ActionButton component for primary actions in the application
 * Can be configured as sticky on mobile with the sticky prop
 */
const ActionButton: React.FC<ActionButtonProps> = ({
  onClick,
  children,
  disabled = false,
  sticky = true,
  variant = "contained",
  color = undefined,
  customColor = "#E65840",
  width = { md: "60%" },
  sx,
  type = "button",
  href,
}) => {
  // Base styles that apply to all buttons
  const baseStyles: SxProps<Theme> = {
    mt: 2,
    mb: 3,
    bgcolor: color ? undefined : customColor,
    color: "white",
    height: "50px",
    borderRadius: "15px",
    width,
    "&:hover": {
      bgcolor: color ? undefined : "#D14A33",
    },
    ...sx,
  };

  // Additional styles for sticky buttons
  const stickyStyles: SxProps<Theme> = sticky
    ? {
        position: { xs: "fixed", sm: "fixed", md: "static" },
        // Clear the fixed price bar, which publishes its measured height as
        // --price-bar-height. It sits at bottom 0 with z-index 1000 against
        // this button's 10, and on a 390px phone its label wraps to two lines
        // (~68px), covering the lower third of the button that takes payment.
        // The old constant 45 assumed one line, which is only true on a wide
        // screen in a short language. The fallback keeps the previous offset
        // on pages that have no price bar.
        bottom: {
          xs: "calc(var(--price-bar-height, 45px) + 8px)",
          sm: "calc(var(--price-bar-height, 40px) + 8px)",
          md: "auto",
        },
        left: { xs: 16, sm: 16, md: "auto" },
        right: { xs: 16, sm: 16, md: "auto" },
        zIndex: { xs: 10, md: "auto" },
        boxShadow: { xs: "0px 4px 8px rgba(0, 0, 0, 0.2)", sm: "none" },
      }
    : {};

  // Combine all styles
  const combinedSx = {
    ...baseStyles,
    ...stickyStyles,
  } as SxProps<Theme>;

  return (
    <Button
      // A sticky CTA: the chat launcher sits above it (chat-widget-placement).
      {...(sticky ? { "data-chat-avoid": "" } : {})}
      onClick={onClick}
      variant={variant}
      color={color}
      disabled={disabled}
      type={type}
      href={href}
      sx={combinedSx}
    >
      {children}
    </Button>
  );
};

export default ActionButton;
